import React, { useState, useEffect } from "react";
import cn from "classnames";
import { isProduction } from "utils";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/webcomponent";
// eslint-disable-next-line import/no-unresolved
import "@oppizi-ecosystem/meeting/styles.css";
import { BackIconStyled, CloseIconStyled } from "./icons";
import CONFIG from "config/config";
import { usePostHog } from "posthog-js/react";
import { CAMPAIGN_ENUM_TYPES } from "shared/constants";
import styles from "./BookingModal.module.css";

type ModalView = "calendar" | "booking" | "success";

interface BookingModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  className?: string;
  submitter: {
    firstName: string;
    lastName: string;
    email: string;
  };
  country?: {
    code: string;
    name?: string;
  };
  onScheduleSuccess?: (reviewMeetingDate: string) => void;
  campaignCost?: number;
  channel?: string;
  campaignId?: string;
  companyName?: string;
  userId?: string;
}

const viewClassMap: Record<ModalView, string> = {
  calendar: "",
  booking: styles.bookingView,
  success: styles.successView,
};

const tabToViewMap: Record<string, ModalView> = {
  calendar: "calendar",
  booking: "booking",
  success: "success",
};

export const BookingModal: React.FC<BookingModalProps> = ({
  isOpen,
  onClose,
  title,
  submitter,
  country,
  onScheduleSuccess,
  campaignCost,
  channel,
  campaignId,
  companyName,
  userId,
}) => {
  const [currentView, setCurrentView] = useState<ModalView>("calendar");
  const posthog = usePostHog();

  useEffect(() => {
    if (!isOpen) return;

    const analyticsData = {
      channel: channel === CAMPAIGN_ENUM_TYPES.DIRECTMAIL ? "EDDM" : channel,
      meetingType: "campaign_review",
      countryName: country?.name,
      countryCode: country?.code,
      amount: campaignCost,
      campaignId,
      companyName,
      userId,
    };

    if (posthog) {
      switch (currentView) {
        case "calendar":
          posthog.capture("Viewed Booking Calendar", analyticsData);
          break;
        case "booking":
          posthog.capture("Viewed Booking Contact Form", analyticsData);
          break;
      }
    }
  }, [currentView, isOpen]);

  const [hideElement, setHideElement] = useState(false);

  useEffect(() => {
    const handleHideElement = () => {
      setHideElement(true);
    };
    document.addEventListener("oppizi-meeting:hide-element-on-success", handleHideElement);

    return () => {
      document.removeEventListener("oppizi-meeting:hide-element-on-success", handleHideElement);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setHideElement(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={cn(styles.modalContainer, viewClassMap[currentView])}>
        <div className={cn(styles.modalHeader)}>
          <div className={styles.headerButtons}>
            <button className={styles.closeButton} onClick={handleClose}>
              <CloseIconStyled />
            </button>
          </div>
        </div>
        <div className={cn(styles.modalContent, viewClassMap[currentView])}>
          {!hideElement && <span className={styles.BookAMeetingTitle}>{title}</span>}
          <oppizi-meeting
            first-name={submitter.firstName}
            last-name={submitter.lastName}
            email={submitter.email}
            country={country?.code}
            redirect-url={`${CONFIG.PLATFORM_BASE_URL}/dashboard/home`}
            ui-mode="embedded"
            is-prod={isProduction()}
          />
        </div>
      </div>
    </div>
  );
};

export default BookingModal;
